import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { action } from 'mobx';
import { breakpoint, ContentSpacing } from '@estee/elc-base-theme';
import { TabHeader } from './TabHeader';
import { TabPanel } from './TabPanel';

export interface ITabs {
    currentTabId: string;
    className?: string;
    onSelect?(currentTabId: string): void;
    renderAllContent?: boolean;
    panelNotTabbable?: boolean;
    keepButtonTabOrder?: boolean;
}

const StyledTabList = styled.div`
    margin: 0 0 ${ContentSpacing.space12};

    ${breakpoint('desktop')`
        display: flex;
    `};
`;

const classNamesTabsList = classnames('elc-tabs__list', 'js-elc-tabs__list');
const classNamesTabPanelsWrapper = classnames(
    'elc-tabs__panels-wrapper',
    'js-elc-tabs__panels-wrapper'
);

export class Tabs extends React.Component<ITabs> {
    private tabHeaderId: string;
    private tabPanelId: string;

    constructor(props: ITabs) {
        super(props);

        const uniqueNumber = new Date().getTime();

        this.tabHeaderId = `elc-tabs__tab-${uniqueNumber}`;

        this.tabPanelId = `elc-tabs__panel-${uniqueNumber}`;
    }

    @action
    private onSelect = (currentTabId: string) => {
        if (this.props.onSelect) {
            this.props.onSelect(currentTabId);
        }
    };

    @action
    private onSelectNext = (currentTabId: string) => {
        const tabIds = this.getTabIds();

        const totalTabs = tabIds.length - 1;

        const index = tabIds.findIndex(tabId => tabId === currentTabId);

        this.onSelect(tabIds[index === totalTabs ? 0 : index + 1]);
    };

    @action
    private onSelectPrevious = (currentTabId: string) => {
        const tabIds = this.getTabIds();

        const totalTabs = tabIds.length - 1;

        const index = tabIds.findIndex(tabId => tabId === currentTabId);

        this.onSelect(tabIds[index === 0 ? totalTabs : index - 1]);
    };

    private getTabIds = () => {
        return React.Children.map(this.getTabs(), (tab: React.ReactElement) => {
            if (tab.props && tab.props.id) {
                return tab.props.id;
            }
        });
    };

    private getTabs = () => {
        return React.Children.toArray(this.props.children).filter(tab => tab);
    };

    public render() {
        const classNamesTabWrapper = classnames('elc-tabs', 'js-elc-tabs', this.props.className);

        const tabs = this.getTabs();
        const { keepButtonTabOrder } = this.props;

        const tabHeaders = React.Children.map(tabs, (tab: React.ReactElement) => {
            const { icon, id, tabHeaderId, tabPanelId, title, subtitle, className } = tab.props;

            return (
                <TabHeader
                    className={className}
                    icon={icon}
                    isCurrent={this.props.currentTabId === id}
                    onSelect={this.onSelect}
                    onSelectNext={this.onSelectNext}
                    onSelectPrevious={this.onSelectPrevious}
                    tabHeaderId={tabHeaderId || `${this.tabHeaderId}-${id}`}
                    tabId={id}
                    tabPanelId={tabPanelId || `${this.tabPanelId}-${id}`}
                    title={title}
                    subtitle={subtitle}
                    keepButtonTabOrder={keepButtonTabOrder}
                />
            );
        });

        const tabPanels = React.Children.map(tabs, (tab: React.ReactElement) => {
            const { currentTabId, renderAllContent, panelNotTabbable } = this.props;
            const { children, id, tabHeaderId, tabPanelId } = tab.props;

            return (
                <TabPanel
                    children={children}
                    isCurrent={currentTabId === id}
                    renderAllContent={renderAllContent}
                    tabHeaderId={tabHeaderId || `${this.tabHeaderId}-${id}`}
                    tabPanelId={tabPanelId || `${this.tabPanelId}-${id}`}
                    panelNotTabbable={panelNotTabbable}
                />
            );
        });

        return (
            <div className={classNamesTabWrapper}>
                <StyledTabList className={classNamesTabsList} role="tablist">
                    {tabHeaders}
                </StyledTabList>
                <div className={classNamesTabPanelsWrapper}>{tabPanels}</div>
            </div>
        );
    }
}
